/**
 * 편도 반납(수령 지점 ≠ 반납 지점) 안내 — 파트너 정책 opt-in (09-22, Bestbike).
 *
 * 파트너가 "편도 반납 요금" 옵션(선택형, 예: 1km당 ¥170)을 등록하고 그 옵션을
 * `products.specifications.oneWayReturnOptionOrder` 에 **display_order 로** 지정하면
 * 켜진다. 옵션 id 가 아니라 display_order 인 이유: 옵션 행은 언어별로 id 가 다르고
 * (`source_option_id` 로만 묶임), 마법사가 저장할 때마다 id 가 새로 발급된다 —
 * 세부 상품/이용 시간 옵션과 같은 "좌표 = display_order" 규약을 따른다.
 *
 * 켜진 상품에서만:
 *  - 반납 지점이 수령 지점으로 기본 선택된다 (`useBookingCart`, 수령 지점이 반납 가능할 때만)
 *  - 수령 ≠ 반납이면 안내(옵션명 + 단가)가 보인다 — 요금은 예약 결제에 포함되지 않고,
 *    예약 확정 후 파트너가 수량(km)을 안내하면 고객이 같은 옵션을 단독 구매한다
 *
 * 지정이 없는 상품(다른 파트너 전부)은 아무 변화가 없다.
 */

import type { Branch, ProductOption, ProductOptionChoice } from "@/types";

export const ONE_WAY_RETURN_SPEC_KEY = "oneWayReturnOptionOrder";

/** 지점이 아닌 수령/반납 방법 센티널 — 편도 판정에서 제외한다. */
const METHOD_SENTINELS = new Set(["delivery", "shipping"]);

export interface OneWayReturnOption {
  option: ProductOption;
  /** 단가를 보여줄 선택지 = 첫 선택지(파트너가 하나만 두는 것을 전제, 여럿이면 최저가). */
  choice: ProductOptionChoice;
}

/** `specifications` 에서 지정된 옵션의 display_order 를 읽는다(없거나 형식이 틀리면 null). */
export function readOneWayReturnOptionOrder(
  specifications: Record<string, unknown> | null | undefined,
): number | null {
  const raw = specifications?.[ONE_WAY_RETURN_SPEC_KEY];
  return typeof raw === "number" && Number.isInteger(raw) && raw >= 0
    ? raw
    : null;
}

/**
 * 상품의 편도 반납 요금 옵션. 지정이 없거나, 지정된 순서에 선택형 옵션이 없거나,
 * 선택지가 비어 있으면 null (= 기능 꺼짐).
 */
export function getOneWayReturnOption(product: {
  specifications?: Record<string, unknown> | null;
  options?: ProductOption[] | null;
}): OneWayReturnOption | null {
  const order = readOneWayReturnOptionOrder(product.specifications);
  if (order == null) return null;
  const option = (product.options ?? []).find(
    (o) => o.display_order === order && o.option_type === "select",
  );
  if (!option || !option.choices || option.choices.length === 0) return null;
  const choice = option.choices.reduce((min, c) =>
    c.price < min.price ? c : min,
  );
  return { option, choice };
}

/** 두 지점 id 가 모두 실제 지점이고 서로 다르면 편도. */
export function isOneWayReturnSelection(
  pickupBranchId: string | null | undefined,
  returnBranchId: string | null | undefined,
): boolean {
  if (!pickupBranchId || !returnBranchId) return false;
  if (METHOD_SENTINELS.has(pickupBranchId) || METHOD_SENTINELS.has(returnBranchId))
    return false;
  return pickupBranchId !== returnBranchId;
}

/** 그 지점에서 반납을 받는지(`location_type` return/both). 센티널·미등록 지점은 false. */
export function canReturnAtBranch(
  branches: Pick<Branch, "id" | "location_type">[] | null | undefined,
  branchId: string,
): boolean {
  if (!branchId || METHOD_SENTINELS.has(branchId)) return false;
  const branch = branches?.find((b) => b.id === branchId);
  return branch?.location_type === "return" || branch?.location_type === "both";
}
